import React from 'react';
import { components } from 'react-select';
import cx from 'classnames';
import has from 'lodash/has';
import map from 'lodash/map';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import without from 'lodash/without';
import { SelectInput } from '../../../rselect/ReactSelectUtils';
import UnsortedSelect from '../../../rselect/UnsortedSelect';

import s from './TransformRulesView.module.scss';

const TransformOption = (props) => {
  const { innerProps, ...otherProps } = props;
  const { data, selectProps } = otherProps;
  const { label = '' } = data;
  const newInnerProps = {
    ...innerProps,
    onMouseOver: () => {
      selectProps.onOptionFocused(data);
    },
  };

  return (
    <components.Option {...otherProps} innerProps={newInnerProps}>
      {label}
    </components.Option>
  );
};

const TransformMenuOptionDetailView = (props) => {
  const { data } = props;
  let label = null,
    descView = null,
    srcListView = null,
    paramsListView = null,
    exampleView = null;
  if (data) {
    label = has(data, 'label') ? data.label : null;
    descView = has(data, 'help') ? (
      <div className="group desc">
        <div className="rule-label">Summary</div>
        <div className="rule-content">{data.help.desc}</div>
      </div>
    ) : null;
    exampleView = has(data, 'help') ? (
      <div className="group desc">
        <div className="rule-label">Example</div>
        <div className="rule-content">{data.help.ex}</div>
      </div>
    ) : null;

    srcListView = has(data, '__srcAttr') ? (
      <div className="group param">
        <div className="rule-label">{data.__srcAttr.display_name}</div>
        <div className="rule-content">{data.__srcAttr.desc}</div>
      </div>
    ) : null;

    paramsListView =
      has(data, 'params') && !isEmpty(data.params)
        ? map(data.params, (p, i) => {
            return (
              <div className="group param" key={i}>
                <div className="rule-label">{p.display_name}</div>
                <div className="rule-content">{p.desc}</div>
              </div>
            );
          })
        : null;
  }

  return (
    <div className="transform-rules-menu-description">
      <div className="group title">
        <div className="rule-label">Function Name</div>
        <div className="rule-content">{label}</div>
      </div>
      {descView}
      <div className="group param-header">
        <div className="rule-label" style={{ textDecoration: 'underline' }}>
          Input Parameters
        </div>
        <div className="param-rule-content">
          {srcListView}
          {paramsListView}
        </div>
      </div>
      {exampleView}
    </div>
  );
};

const TransformMenu = (props) => {
  const {
    selectProps: { value, optionInFocus },
  } = props;

  const data = !isNil(optionInFocus) ? optionInFocus : !isNil(value) ? value : null;

  return (
    <components.Menu {...props}>
      {props.children}
      <TransformMenuOptionDetailView data={data} />
    </components.Menu>
  );
};

const rootStyle = {
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  baseControlStyle: {
    height: 'fit-content',
    background: '#ffffff',
    borderRadius: 8,
    border: '1px solid #DFE8F0',
    cursor: 'pointer',
    paddingRight: 8,
    fontSize: 14,
    color: '#2f3037',
  },
};

const getUnsortedSelectStyle = (isRoot) => {
  return isRoot === true
    ? {
        menuPortal: (styles) => ({ ...styles, fontSize: 12 }),
        control: (styles) => ({
          ...styles,
          ...rootStyle.baseControlStyle,
          flex: 1,
          width: '100%',
        }),
        groupHeading: (styles) => ({ ...styles, color: '#ff7436' }),
      }
    : {
        menuPortal: (styles) => ({ ...styles, fontSize: 12 }),
        control: (styles) => ({
          ...styles,
          ...rootStyle.baseControlStyle,
          alignSelf: 'flex-end',
          marginTop: -40,
          width: '69%',
          flex: 1,
          marginLeft: 8,
          maxHeight: 'fit-content',
        }),
        groupHeading: (styles) => ({ ...styles, color: '#ff7436' }),
        container: (styles) => ({ ...styles, ...rootStyle.container }),
      };
};

class TransformRulesView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      optionInFocus: null,
    };

    this.onOptionSelected = this.onOptionSelected.bind(this);
    this.onOptionFocused = this.onOptionFocused.bind(this);
    this.getTransformOptions = this.getTransformOptions.bind(this);
  }

  onOptionSelected(newVal) {
    this.props.onTransformSelected(newVal);
  }

  onOptionFocused(option) {
    const optionInFocus = !isNil(option) ? option : null;
    this.setState({
      optionInFocus,
    });
  }

  getTransformOptions() {
    const { isRoot, allowedTransforms, groupedTransformRules } = this.props;

    //only allow noop at first level of transform rules
    const { groupOrder, groupRules } = allowedTransforms;

    return reduce(
      groupOrder,
      (acc, currGroup) => {
        const thisGroup = groupRules[currGroup];
        const validRules =
          isRoot === false && currGroup === 'miscGroup'
            ? without(thisGroup.optionsOrder, 'eqAttr')
            : thisGroup.optionsOrder;

        let thisGroupOptions = map(validRules, (o) => groupedTransformRules.rules[o]);
        acc.push({
          label: thisGroup.label,
          options: thisGroupOptions,
        });
        return acc;
      },
      [],
    );
  }

  render() {
    const { isRoot } = this.props;
    const selectStyle = getUnsortedSelectStyle(isRoot);
    const options = this.getTransformOptions();

    return (
      <div
        aria-label="TransformRulesView"
        className={cx({
          [s.rules_view]: true,
          [s.transform_rules_container]: true,
        })}
      >
        <UnsortedSelect
          classNamePrefix="transform-rules"
          value={this.props.selectedTransform}
          placeholder="Type or select..."
          options={options}
          components={{
            Option: TransformOption,
            Menu: TransformMenu,
            Input: SelectInput,
          }}
          isClearable={false}
          backspaceRemovesValue={false}
          styles={selectStyle}
          variant="default"
          isRootMenu={isRoot}
          isOptionDisabled={(option) => option.isSectionHeader === true}
          onChange={this.onOptionSelected}
          onOptionFocused={this.onOptionFocused}
          optionInFocus={this.state.optionInFocus}
          onMenuScrollToBottom={this.props.onLoadReusableTransforms}
        />
      </div>
    );
  }
}

export default TransformRulesView;
